import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dmClient } from '../../services/dmClient';
import './DenialIntegrationsPage.css';

const GROUP_LABELS = {
  cm_standard: 'CM Standard Input',
  raw: 'Raw EDI (835 / 837)',
  documents: 'Clinical Documents',
  output: 'Expected Output',
  datawarehouse: 'Data Warehouse',
  shared: 'Shared Exports',
  mapping: 'Ingestion Mapping',
  other: 'Other',
};

const GROUP_ORDER = [
  'cm_standard', 'raw', 'documents', 'output',
  'datawarehouse', 'shared', 'mapping', 'other',
];

const formatBytes = (n) => {
  if (n === null || n === undefined) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const formatValue = (v) => {
  if (v === null || v === undefined) return '—';
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const formatCurrency = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
};

function CsvTable({ csv }) {
  if (!csv || !csv.headers || csv.headers.length === 0) {
    return <div className="di-empty">No tabular data.</div>;
  }
  return (
    <div className="di-csv-wrap">
      <table className="di-csv">
        <thead>
          <tr>{csv.headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {csv.rows.map((row, ri) => (
            <tr key={ri}>
              {csv.headers.map((_, ci) => <td key={ci}>{row[ci] ?? ''}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FileViewer({ file }) {
  if (!file) {
    return <div className="di-empty">Select a file to preview its data.</div>;
  }
  if (file.kind === 'csv') {
    return <CsvTable csv={file.csv} />;
  }
  if ((file.kind === 'json' || file.kind === 'jsonl') && file.json !== undefined) {
    return (
      <pre className="di-code di-code--json">
        {JSON.stringify(file.json, null, 2)}
      </pre>
    );
  }
  return <pre className="di-code">{file.text}</pre>;
}

function SourceFilesTab({ files, selectedPath, onSelect }) {
  const grouped = useMemo(() => {
    const map = {};
    (files || []).forEach((f) => {
      (map[f.group] = map[f.group] || []).push(f);
    });
    return map;
  }, [files]);

  const selected = (files || []).find((f) => f.path === selectedPath) || null;

  return (
    <div className="di-files">
      <div className="di-files-list">
        {GROUP_ORDER.filter((g) => grouped[g]).map((g) => (
          <div key={g} className="di-files-group">
            <div className="di-files-group-title">{GROUP_LABELS[g] || g}</div>
            {grouped[g].map((f) => (
              <button
                key={f.path}
                type="button"
                className={`di-file-item${f.path === selectedPath ? ' is-active' : ''}`}
                onClick={() => onSelect(f.path)}
                title={f.path}
              >
                <span className="di-file-name">{f.name}</span>
                <span className="di-file-size">{formatBytes(f.size)}</span>
              </button>
            ))}
          </div>
        ))}
      </div>
      <div className="di-files-preview">
        {selected && (
          <div className="di-preview-head">
            <span className="di-preview-path">{selected.path}</span>
            <span className={`di-badge di-badge--${selected.kind}`}>{selected.kind}</span>
          </div>
        )}
        <div className="di-preview-body">
          <FileViewer file={selected} />
        </div>
      </div>
    </div>
  );
}

function MappingTab({ mapping }) {
  if (!mapping) return <div className="di-empty">No mapping available.</div>;
  return (
    <div className="di-mapping">
      <section className="di-mapping-section">
        <h3>File-level mapping <span>(mapping/ingestion_map.csv)</span></h3>
        <table className="di-table">
          <thead>
            <tr><th>Source file</th><th>Source type</th><th>Maps to</th></tr>
          </thead>
          <tbody>
            {(mapping.files || []).map((r, i) => (
              <tr key={i}>
                <td className="di-mono">{r.source_file}</td>
                <td>{r.source_type}</td>
                <td>{r.maps_to}</td>
              </tr>
            ))}
            {(mapping.files || []).length === 0 && (
              <tr><td colSpan={3} className="di-empty">No ingestion_map.csv in pack.</td></tr>
            )}
          </tbody>
        </table>
      </section>

      <section className="di-mapping-section">
        <h3>Field-level mapping <span>(CM standard &rarr; engine denial_payload)</span></h3>
        <table className="di-table">
          <thead>
            <tr><th>CM standard field</th><th>denial_payload field</th><th>Value</th></tr>
          </thead>
          <tbody>
            {(mapping.fields || []).map((r, i) => (
              <tr key={i}>
                <td className="di-mono">{r.source_field}</td>
                <td className="di-mono">{r.denial_payload_field}</td>
                <td>{formatValue(r.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function StandardJsonTab({ cmStandard, denialPayload, variant }) {
  return (
    <div className="di-json-cols">
      <div className="di-json-col">
        <div className="di-json-col-head">
          cm_standard_input.json
          {variant && <span className="di-badge di-badge--variant">{variant}</span>}
        </div>
        <pre className="di-code di-code--json">
          {JSON.stringify(cmStandard, null, 2)}
        </pre>
      </div>
      <div className="di-json-col">
        <div className="di-json-col-head">
          denial_payload <span className="di-badge di-badge--submit">submitted</span>
        </div>
        <pre className="di-code di-code--json">
          {JSON.stringify(denialPayload, null, 2)}
        </pre>
      </div>
    </div>
  );
}

function CasesTable({ cases, selectedIdx, onSelect, submitStatus, onSubmit }) {
  return (
    <div className="di-table-wrap">
      <table className="di-table di-cases-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Case ID</th>
            <th>Denial Type</th>
            <th>Complexity</th>
            <th>Payer</th>
            <th>Denied $</th>
            <th>Files</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {cases.map((c, i) => {
            const m = c.submitMeta || {};
            const status = submitStatus[c.caseId] || 'idle';
            return (
              <tr
                key={c.cmStandardPath || i}
                className={`di-case-row${i === selectedIdx ? ' is-active' : ''}`}
                onClick={() => onSelect(i)}
              >
                <td>{i + 1}</td>
                <td className="di-mono">{c.caseId || '—'}</td>
                <td>{m.archetype || '—'}</td>
                <td>{m.complexity || '—'}</td>
                <td>{m.payerName || '—'}</td>
                <td>{formatCurrency(m.totalDeniedAmount)}</td>
                <td>{c.fileTotal}</td>
                <td>
                  {status === 'done' && <span className="di-status di-status--done">Submitted</span>}
                  {status === 'error' && <span className="di-status di-status--error">Failed</span>}
                  {(status === 'idle' || status === 'submitting') && (
                    <span className="di-status">{status === 'submitting' ? 'Submitting…' : 'Not submitted'}</span>
                  )}
                </td>
                <td>
                  <button
                    type="button"
                    className="di-btn di-btn--primary di-row-submit"
                    disabled={status === 'submitting' || status === 'done'}
                    onClick={(e) => { e.stopPropagation(); onSubmit(c); }}
                  >
                    {status === 'done' ? '✓ Submitted' : status === 'submitting' ? 'Submitting…' : 'Submit'}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function DenialIntegrationsPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState(null);
  const [cases, setCases] = useState([]);
  const [sharedFiles, setSharedFiles] = useState([]);
  const [caseCount, setCaseCount] = useState(0);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [activeTab, setActiveTab] = useState('files');
  const [selectedPath, setSelectedPath] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({});
  const [submitMsg, setSubmitMsg] = useState(null);

  const selectedCase = cases[selectedIdx] || null;

  // Source Files tab shows the case-specific files plus the pack-wide shared files.
  const filesForSelected = useMemo(() => {
    if (!selectedCase) return [];
    return [...(selectedCase.files || []), ...sharedFiles];
  }, [selectedCase, sharedFiles]);

  const selectCase = useCallback((idx) => {
    setSelectedIdx(idx);
    setActiveTab('files');
    const c = cases[idx];
    const list = [...(c?.files || []), ...sharedFiles];
    const firstStd = list.find((f) => f.group === 'cm_standard');
    setSelectedPath(firstStd?.path || (list[0]?.path ?? null));
  }, [cases, sharedFiles]);

  const handleParse = useCallback(async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.zip')) {
      setError('Please upload a .zip file containing the denial input pack.');
      return;
    }
    setParsing(true);
    setError(null);
    setCases([]);
    setSharedFiles([]);
    setSubmitStatus({});
    setSubmitMsg(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const resp = await dmClient.post('/denial-integrations/parse', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      const data = resp.data?.data || {};
      const shared = data.sharedFiles || [];
      const parsedCases = (data.cases || []).map((c) => ({
        ...c,
        fileTotal: (c.files || []).length + shared.length,
      }));
      setCases(parsedCases);
      setSharedFiles(shared);
      setCaseCount(data.caseCount || parsedCases.length);
      setSelectedIdx(0);
      setActiveTab('files');
      const list = [...(parsedCases[0]?.files || []), ...shared];
      const firstStd = list.find((f) => f.group === 'cm_standard');
      setSelectedPath(firstStd?.path || (list[0]?.path ?? null));
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || 'Failed to parse the pack.';
      setError(msg);
    } finally {
      setParsing(false);
    }
  }, []);

  const onFileInput = (e) => {
    const file = e.target.files?.[0];
    handleParse(file);
    e.target.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    handleParse(file);
  };

  const handleSubmit = useCallback(async (caseObj) => {
    if (!caseObj) return;
    const id = caseObj.caseId;
    setSubmitStatus((prev) => ({ ...prev, [id]: 'submitting' }));
    setSubmitMsg(null);
    setError(null);
    try {
      const meta = caseObj.submitMeta || {};
      // Clinical documents from this case become sim-EHR notes the engine can
      // fetch as evidence. Only send files that carry readable text.
      const documents = (caseObj.files || [])
        .filter((f) => f.group === 'documents' && f.text)
        .map((f) => ({
          name: f.name,
          path: f.path,
          kind: f.kind,
          text: f.text,
        }));
      const resp = await dmClient.post('/denial-integrations/ingest', {
        payload: caseObj.denialPayload,
        meta,
        documents,
      });
      setSubmitStatus((prev) => ({ ...prev, [id]: 'done' }));
      const newCaseId = resp.data?.caseId || resp.data?.data?.caseId;
      if (newCaseId) {
        // Take the user straight to the live processing view; the case page
        // auto-starts the Denial Recovery Engine and streams progress.
        navigate(`/denials/${newCaseId}`, { state: { autostart: true } });
        return;
      }
      // Fallback (no id returned): keep the prior banner + link behavior.
      setSubmitMsg(`Case ${id} submitted to the denial workflow.`);
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || 'Failed to submit case.';
      setSubmitStatus((prev) => ({ ...prev, [id]: 'error' }));
      setError(`Failed to submit ${id}: ${msg}`);
    }
  }, [navigate]);

  const meta = selectedCase?.submitMeta || {};
  const selStatus = selectedCase ? (submitStatus[selectedCase.caseId] || 'idle') : 'idle';

  return (
    <div className="di-page">
      <div className="di-header">
        <div>
          <button type="button" className="di-back" onClick={() => navigate('/denials')}>
            &larr; Back to Denials
          </button>
          <h1>Data Integrations</h1>
          <p>
            Upload a zip of a denial input pack (extracted from source healthcare
            systems). The system unzips it, lists each case it contains, shows every
            source file, maps the values into the CM standard, and submits the
            normalized case to the denial workflow.
          </p>
        </div>
      </div>

      <div
        className={`di-dropzone${dragOver ? ' is-drag' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        role="button"
        tabIndex={0}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".zip"
          style={{ display: 'none' }}
          onChange={onFileInput}
        />
        <div className="di-dropzone-icon">⤓</div>
        <div className="di-dropzone-text">
          {parsing
            ? 'Parsing pack…'
            : 'Drag & drop the input pack .zip here, or click to choose a file'}
        </div>
        {cases.length > 0 && (
          <div className="di-dropzone-sub">
            Loaded <strong>{caseCount}</strong> case{caseCount === 1 ? '' : 's'}
            {' '}and {sharedFiles.length} shared file{sharedFiles.length === 1 ? '' : 's'}.
          </div>
        )}
      </div>

      {error && <div className="di-alert di-alert--error">{error}</div>}
      {submitMsg && (
        <div className="di-alert di-alert--ok">
          {submitMsg}{' '}
          <button type="button" className="di-link" onClick={() => navigate('/denials')}>
            View in Denials →
          </button>
        </div>
      )}

      {cases.length > 0 && (
        <>
          <div className="di-section-title">Cases in pack ({cases.length})</div>
          <CasesTable
            cases={cases}
            selectedIdx={selectedIdx}
            onSelect={selectCase}
            submitStatus={submitStatus}
            onSubmit={handleSubmit}
          />
        </>
      )}

      {selectedCase && (
        <>
          <div className="di-detail-head">
            <div className="di-section-title">
              Case detail: <span className="di-mono">{selectedCase.caseId}</span>
              <span className="di-badge di-badge--variant">{selectedCase.variant}</span>
            </div>
          </div>

          <div className="di-summary">
            <div className="di-summary-item">
              <span className="di-summary-label">Case</span>
              <span className="di-summary-value">{meta.externalId || '—'}</span>
            </div>
            <div className="di-summary-item">
              <span className="di-summary-label">Denial Type</span>
              <span className="di-summary-value">{meta.archetype || '—'}</span>
            </div>
            <div className="di-summary-item">
              <span className="di-summary-label">Complexity</span>
              <span className="di-summary-value">{meta.complexity || '—'}</span>
            </div>
            <div className="di-summary-item">
              <span className="di-summary-label">Payer</span>
              <span className="di-summary-value">{meta.payerName || '—'}</span>
            </div>
            <div className="di-summary-item">
              <span className="di-summary-label">Denied $</span>
              <span className="di-summary-value">{formatCurrency(meta.totalDeniedAmount)}</span>
            </div>
            <button
              type="button"
              className="di-btn di-btn--primary di-summary-submit"
              onClick={() => handleSubmit(selectedCase)}
              disabled={selStatus === 'submitting' || selStatus === 'done'}
            >
              {selStatus === 'done'
                ? '✓ Submitted'
                : selStatus === 'submitting'
                  ? 'Submitting…'
                  : 'Submit to Denial Workflow'}
            </button>
          </div>

          <div className="di-tabs">
            <button
              type="button"
              className={`di-tab${activeTab === 'files' ? ' is-active' : ''}`}
              onClick={() => setActiveTab('files')}
            >Source Files</button>
            <button
              type="button"
              className={`di-tab${activeTab === 'mapping' ? ' is-active' : ''}`}
              onClick={() => setActiveTab('mapping')}
            >Field Mapping</button>
            <button
              type="button"
              className={`di-tab${activeTab === 'standard' ? ' is-active' : ''}`}
              onClick={() => setActiveTab('standard')}
            >CM Standard JSON</button>
          </div>

          <div className="di-tab-body">
            {activeTab === 'files' && (
              <SourceFilesTab
                files={filesForSelected}
                selectedPath={selectedPath}
                onSelect={setSelectedPath}
              />
            )}
            {activeTab === 'mapping' && <MappingTab mapping={selectedCase.mapping} />}
            {activeTab === 'standard' && (
              <StandardJsonTab
                cmStandard={selectedCase.cmStandard}
                denialPayload={selectedCase.denialPayload}
                variant={selectedCase.variant}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default DenialIntegrationsPage;
